import { TableBadgeLabel, useModalContentToggler } from '@components'

import { Button, Space, Table, TableColumn } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TableIds } from '../../../../../../enums/tableIds'
import { CustomerModalState } from '../../../../enums/CustomerModalState'
import { CustomerType } from '../../../../enums/customerType'
import { getContactPersonsWithoutEmptyPrimaryContact } from '../../../../utils/getContactPersonsWithoutEmptyPrimaryContact'
import { getContactPersonsWithoutPrimaryContact } from '../../../../utils/getContactPersonsWithoutPrimaryContact'
import { useCustomerForm } from '../../contexts/customerFormContext'
import { useContactPersonFormActions } from '../../hooks/useContactPersonFormActions'
import { CompanyContactPersonsTableRow } from '../../types/companyContactPersonsTableRow'
import { useContactPersonsTableColumns } from './elements/ContactPersonsTable/hooks/useContactPersonsTableColumns'
import { getCompanyContactPersonsTableData } from './elements/ContactPersonsTable/utils/getCompanyContactPersonsTableData'
import * as Styled from './styles'

export const ContactPersons = (): ReactElement => {
  const { t } = useTranslation()
  const { activeState, setActiveState } = useModalContentToggler()
  const { customerType, customerForm, contactPersons } = useCustomerForm()
  const isContactPersonsEmpty = contactPersons.length === 0
  const { resetContactFormFields } = useContactPersonFormActions(customerForm)
  const columns: TableColumn<CompanyContactPersonsTableRow>[] = useContactPersonsTableColumns()

  const filteredContactPersons = useMemo(() => {
    return customerType === CustomerType.Individual
      ? getContactPersonsWithoutPrimaryContact(contactPersons)
      : getContactPersonsWithoutEmptyPrimaryContact(contactPersons)
  }, [contactPersons, customerType])

  const tableData = useMemo(() => getCompanyContactPersonsTableData(filteredContactPersons), [filteredContactPersons])

  const addContactPersonButtonLabel = useMemo(
    () =>
      activeState === CustomerModalState.Individual
        ? t('customer_modal.form.company_persons.individual_button.label')
        : t('customer_modal.form.company_persons.company_button.label'),
    [activeState, t],
  )

  const filteredContactPersonsCount = filteredContactPersons.length
  const shouldShowContactPersons = !isContactPersonsEmpty && filteredContactPersonsCount !== 0

  const handleAddContactPerson = useCallback(() => {
    resetContactFormFields()
    setActiveState(CustomerModalState.AddContactPerson)
  }, [resetContactFormFields, setActiveState])

  return (
    <Styled.ContactPersonsWrapper>
      {shouldShowContactPersons && (
        <>
          <Space size="s24" />
          <TableBadgeLabel amount={filteredContactPersonsCount} color="neutral">
            {t('customer_modal.form.company_persons.label')}
          </TableBadgeLabel>
          <Space size="s16" />
          <Styled.ContactPersonsTableWrapper>
            <Table columns={columns} data={tableData} id={TableIds.ContactPersonsList} withColumnsFiltering={false} />
          </Styled.ContactPersonsTableWrapper>
          <Space size="s16" />
        </>
      )}
      <Button icon="circlePlus" onClick={handleAddContactPerson} variant="outlined">
        {addContactPersonButtonLabel}
      </Button>
      <Space size="s24" />
    </Styled.ContactPersonsWrapper>
  )
}
